import React from 'react';
import PropTypes from 'prop-types';
import equals from 'equals'
import DOMPurify from 'dompurify';

import BodyPortal from '../portals/body-portal';

import OverlayUIElement from './overlay-ui-element';
import PromptPopout from './prompt-popout';
import Tooltip from './tooltip';
import FieldDefault from './field-default';

import IconInfo from 'icons/icon-info';
import IconTick from 'icons/icon-tick';

export default class Dropdown extends OverlayUIElement {
    static propTypes = {
        className: PropTypes.string,
        items: PropTypes.array,
        selectable: PropTypes.bool,
        getWidth: PropTypes.func,
        onShow: PropTypes.func,
        onItemClick: PropTypes.func,
        onEnterPress: PropTypes.func,
        provideController: PropTypes.func
    };

    static defaultProps = {
        className: '',
        items: [],
        showArrow: false
    };

    constructor(props) {
        super(props);

        if (props.selectable && props.items.length) {
            props.items[0].selectionActive = true;
        }

        this.state = {
            items: props.items,
            visible: false,
            zIndex: 900,
            searchTerm: ''
        };
    }

    handlePromptShow = () => {
        this.setState({
            promptVisible: true
        });
    }

    handlePromptHide = () => {
        this.setState({
            promptVisible: false
        });
    }

    handleSearchTermChange = searchTerm => {
        this.setState({ searchTerm }, () => {
            this.positionOverlayElement();
        });
    }

    getCurrentSelection() {
        return this.items.filter(item => item.selectionActive)[0];
    }

    selectionUp() {
        const currentSelection = this.getCurrentSelection();
        const targetItemIndex = this.items.indexOf(currentSelection);
        let targetItem = this.items[targetItemIndex - 1];

        if (targetItem && targetItem.header) {
            targetItem = this.items[targetItemIndex - 2];
        }

        if (!currentSelection || targetItemIndex <= 0) {
            return;
        }

        targetItem.selectionActive = true;
        currentSelection.selectionActive = false;

        this.setState({
            items: this.state.items
        });
    }

    selectionDown() {
        const currentSelection = this.getCurrentSelection();
        const targetItemIndex = this.items.indexOf(currentSelection);
        let targetItem = this.items[targetItemIndex + 1];

        if (targetItem && targetItem.header) {
            targetItem = this.items[targetItemIndex + 2];
        }

        if (!currentSelection || targetItemIndex < 0 || targetItemIndex >= this.items.length - 1) {
            return;
        }

        targetItem.selectionActive = true;
        currentSelection.selectionActive = false;

        this.setState({
            items: this.state.items
        });
    }

    getDropdownItemInner(item) {
        if (this.props.itemComponent) {
            return React.cloneElement(this.props.itemComponent, { ...item });
        }

        const elements = [];

        if (item.info) {
            elements.push(
                <span key="info" className="info tooltip-trigger">
                    <IconInfo />

                    <span className="tooltip" dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(item.info)}} />
                </span>
            );
        }

        if (item.image) {
            elements.push(<span key="image" className="image" style={{backgroundImage: `url('${item.image}')`}}></span>);
        }

        if (item.icon) {
            if (typeof item.icon === 'string') {
                elements.push(<i key="icon" className={item.icon} />);
            } else {
                elements.push(item.icon);
            }
        }

        if (item.material) {
            elements.push(<i key="icon" className="icon material-icons">{item.material}</i>);
        }

        if (item.link) {
            elements.push(<a key="content" href={item.link}>{item.text}</a>);
        } else {
            elements.push(<span key="content">{item.text}</span>);
        }

        elements.push(
            <span key="marker" className="selection-marker">
                <IconTick />
            </span>
        );

        return elements;
    }

    show() {
        if (this.state.visible) {
            return;
        }

        let width;

        if (this.props.getWidth) {
            width = this.props.getWidth();
        }

        this.setState({
            visible: true,
            width
        }, () => {
            const parent = this.overlayPlaceholder ? this.overlayPlaceholder.parentNode : null;

            if (parent && this.props.width === 'inherit') {
                this.element.style.width = `${parent.clientWidth}px`;
            }

            this.setOverlayElement(this.props.refElement || this.element, parent);
            this.handleOverlayElementShow();
        });


        this.props.onShow && this.props.onShow();
    }

    hide() {
        if (!this.state.visible) {
            return;
        }

        this.setState({
            visible: false
        });

        this.handleOverlayElementHide();

        this.props.onHide && this.props.onHide();
    }

    get items() {
        if (this.state.searchTerm) {
            const searchTerm = this.state.searchTerm.toLowerCase();

            return this.state.items.filter(item => item.text && item.text.toString().toLowerCase().indexOf(searchTerm) >= 0);
        }

        return this.state.items;
    }

    componentDidMount() {
        this.props.provideController && this.props.provideController({
            show: () => {
                this.show();
            },
            hide: () => {
                this.hide();
            }
        });

        const handleKeydown = (event) => {
            if (!this.state.visible) {
                return;
            }

            switch (event.which) {
                case 40: // Down
                    this.selectionDown();
                    break;

                case 38: // Up
                    this.selectionUp();
                    break;

                case 13: // Enter
                    this.props.onEnterPress && this.props.onEnterPress(this.getCurrentSelection());
                    break;

                case 27: // Esc
                    this.hide();
                    break;

                default:
                    return null;
            }
        }

        this.removeEventListener = () => window.removeEventListener('keydown', handleKeydown);;

        window.addEventListener('keydown', handleKeydown);

        this.setOverlayElement(this.element, this.overlayPlaceholder ? this.overlayPlaceholder.parentNode : null);
    }

    shouldComponentUpdate(nextProps, nextState) {
        if (!equals(this.props.items, nextProps.items)) return true;
        if (!equals(this.state, nextState)) return true;

        return false;
    }

    componentDidUpdate(prevProps, prevState) {
        if (this.state.visible && !prevState.visible) {
            this.setState({
                zIndex: Array.from(document.querySelectorAll('body > .visible')).reduce((maxZIndex, element) => Math.max(element.style.zIndex, maxZIndex), 900) + 1
            });
        }

        if (prevProps.items !== this.props.items) {
            if (this.props.selectable && this.props.items.length) {
                this.props.items[0].selectionActive = true;
            }

            this.setState({
                items: this.props.items
            });
        }
    }

    componentWillUnmount() {
        this.hide();
        this.handleOverlayParentDestroy();

        this.removeEventListener && this.removeEventListener();

        this.props.provideController && this.props.provideController(null);
    }

    render() {
        const style = {
            zIndex: this.state.zIndex
        };

        if (this.props.useMinWidth) {
            style.minWidth = `${this.state.width}px`;
        } else {
            style.width = `${this.state.width}px`;
        }

        return (
            <span className="overlay-placeholder" ref={element => this.overlayPlaceholder = element}>
                {
                    (this.state.visible || this.state.promptVisible || this.props.keepVisible || this.items.some(item => item.prompt)) ? (
                        <BodyPortal>
                            <div ref={element => this.element = element} style={style} className={'dropdown ' + this.props.className + (this.state.visible ? ' visible' : '') + (this.props.selectable ? ' selectable' : '') + (this.props.showArrow ? ' show-arrow' : '')}>
                                {
                                    this.props.title ? <div className="dropdown-title">{this.props.title}</div> : null
                                }

                                <div className="dropdown-inner">
                                    {
                                        this.props.withSearch ? (
                                            <div className="dropdow-search">
                                                <FieldDefault onChange={this.handleSearchTermChange} value={this.state.searchTerm} placeholder="Type to search..." />
                                            </div>
                                        ) : null
                                    }

                                    <ul>
                                        {
                                            this.items.map((item, index) => {
                                                const itemStyles = (item.style || []).map(s => `style-${s}`).join(' ');
                                                if (item.component) {
                                                    return (
                                                        <li
                                                            key={index}
                                                            className="dropdown-item"
                                                        >
                                                            {item.component}
                                                        </li>
                                                    )
                                                }

                                                if (item.header) {
                                                    return (
                                                        <li
                                                            key={index}
                                                            className={`dropdown-item item-header ${itemStyles}`}
                                                        >
                                                            {item.title}
                                                        </li>
                                                    )
                                                }

                                                const element = <li
                                                    key={index}
                                                    onClick={this.props.onItemClick && this.props.onItemClick.bind(this, item)}
                                                    className={'dropdown-item' + (item.selected ? ' selected' : '') + (item.selectionActive ? ' selection-active' : '') + ' ' + itemStyles}
                                                    data-notification={item.notificationsCount}
                                                >
                                                    {this.getDropdownItemInner(item)}
                                                </li>;

                                                if (item.prompt) {
                                                    return (
                                                        <PromptPopout
                                                            key={index}
                                                            title={item.prompt.title}
                                                            text={item.prompt.text}
                                                            confirmText={item.prompt.confirmText}
                                                            confirmClassName={item.prompt.confirmClassName}
                                                            cursorPosition={true}
                                                            onShow={this.handlePromptShow}
                                                            onHide={this.handlePromptHide}
                                                        >
                                                            {element}
                                                        </PromptPopout>
                                                    );
                                                }

                                                if (item.tooltip) {
                                                    return (
                                                        <Tooltip key={index} tooltip={item.tooltip}>
                                                            { element }
                                                        </Tooltip>
                                                    );
                                                }

                                                return element;
                                            })
                                        }
                                    </ul>
                                </div>
                            </div>
                        </BodyPortal>
                    ) : null
                }
            </span>
        );
    }
}
